/**
 * Threadverse Search — Theme Integration
 * Overrides Shopify's native predictive search with Threadverse API.
 * Falls back to Shopify search on error. No dependencies.
 *
 * All 7 issues from Threadverse backend audit addressed:
 * 1. sendBeacon: wrapped payload in Blob with application/json MIME type
 * 2. Badge class XSS: sanitised to [a-z0-9-] only
 * 3. x-tabs layout: renders <x-tabs> with slot="title" + slot="content" pattern
 * 4. Vendor: reads data-threadverse-show-vendor from <predictive-search>
 * 5. Fallback timeout: 5s AbortController on Shopify fallback fetch
 * 6. Idle state restore: fallback restores idle slot when parse fails
 * 7. section-id null guard: gracefully restores idle if attribute missing
 */
(function () {
  'use strict';

  // Cached idle state data, populated on first load. Reused by the
  // empty-results state so a failed search shows the same Popular Now +
  // Trending recovery layout as the just-opened drawer.
  var cachedIdleData = null;
  var analyticsTimer = null;
  var lastLoggedQuery = null;

  // Shopper-facing copy comes from the locale files via window.threadheads.strings
  // (snippets/js-variables.liquid); the English fallbacks match those locale values.
  function str(key, fallback) {
    var strings = (window.threadheads && window.threadheads.strings) || {};
    return strings[key] || fallback;
  }

  // Function replacement so a "$&" in the search query is inserted literally.
  function fill(template, token, value) {
    return template.replace(token, function () { return value; });
  }

  // Session ID — persistent per browser session
  var SESSION_KEY = 'threadverse_search_session';
  var memSessionId = null;
  function getSessionId() {
    try {
      var id = sessionStorage.getItem(SESSION_KEY);
      if (!id) {
        id = 'tv-' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
        sessionStorage.setItem(SESSION_KEY, id);
      }
      return id;
    } catch (e) {
      // Storage blocked (Safari Private, in-app browsers): one id for this page, and no page error.
      if (!memSessionId) memSessionId = 'tv-' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
      return memSessionId;
    }
  }

  // The visitor's Shopify Market is baked into the API base URL (data-threadverse-api
  // → ".../m/<market>") by the theme, so the search endpoint already knows the
  // market. Nothing market-related is needed in JS.

  // Money formatter — handles all Shopify format variants
  function formatMoney(priceStr) {
    var format = window.themeVariables && window.themeVariables.settings
      ? window.themeVariables.settings.moneyFormat
      : '${{amount}}';
    var num = parseFloat(priceStr || '0');
    var amount = num.toFixed(2);
    var amountNoDecimals = Math.round(num).toString();
    var amountWithComma = amount.replace('.', ',');
    var amountNoDecimalsWithComma = amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

    return format
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amountWithComma)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, amountNoDecimalsWithComma)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, amountNoDecimals)
      .replace(/\{\{\s*amount\s*\}\}/, amount);
  }

  // Safe text node helper
  function text(str) {
    return document.createTextNode(str || '');
  }

  // Create element helper
  function el(tag, className, attrs) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (attrs) {
      for (var key in attrs) {
        if (attrs.hasOwnProperty(key)) node.setAttribute(key, attrs[key]);
      }
    }
    return node;
  }

  // Sanitise badge class name — alphanumeric and hyphens only (XSS prevention)
  function sanitiseBadgeClass(badge) {
    return (badge || '').replace(/[^a-z0-9-]/gi, '');
  }

  // Fire analytics event (fire-and-forget)
  function trackEvent(apiUrl, eventData) {
    eventData.session_id = getSessionId();
    try {
      var payload = JSON.stringify(eventData);
      if (navigator.sendBeacon) {
        // Use Blob with correct content type for sendBeacon
        var blob = new Blob([payload], { type: 'application/json' });
        navigator.sendBeacon(apiUrl + '/search/event', blob);
      } else {
        fetch(apiUrl + '/search/event', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: payload,
          keepalive: true
        }).catch(function () {});
      }
    } catch (e) { /* never fail on analytics */ }
  }

  function scheduleSearchAnalytics(apiUrl, query, resultsCount) {
    if (analyticsTimer) clearTimeout(analyticsTimer);
    analyticsTimer = setTimeout(function () {
      var q = (query || '').trim();
      if (q.length < 3 || q === lastLoggedQuery) return;
      lastLoggedQuery = q;
      trackEvent(apiUrl, {
        event_type: resultsCount === 0 ? 'zero_results' : 'search',
        query: q,
        results_count: resultsCount
      });
    }, 400);
  }

  // Check if vendor should be shown — reads data-threadverse-show-vendor from the component
  function shouldShowVendor(component) {
    if (component && component.dataset && component.dataset.threadverseShowVendor) {
      return component.dataset.threadverseShowVendor === 'true';
    }
    return false;
  }

  // Build product card DOM matching theme CSS classes
  function buildProductCard(product, position, apiUrl, query, component) {
    var link = el('a', 'predictive-search-result group', {
      href: product.url,
      'data-instant': ''
    });

    // Image
    if (product.image_url) {
      var imgWrap = el('div', 'predictive-search-result__image');
      var img = el('img');
      img.src = product.image_url.includes('?')
        ? product.image_url + '&width=400'
        : product.image_url + '?width=400';
      img.alt = product.title || '';
      img.loading = 'lazy';
      img.width = 400;
      imgWrap.appendChild(img);

      // Badge overlay — sanitised class names
      if (product.badges && product.badges.length > 0) {
        var badgeWrap = el('div', 'th-search-badges');
        product.badges.forEach(function (badge) {
          var safeClass = sanitiseBadgeClass(badge);
          if (!safeClass) return;
          var pill = el('span', 'th-search-badge th-search-badge--' + safeClass);
          pill.textContent = badge.charAt(0).toUpperCase() + badge.slice(1);
          badgeWrap.appendChild(pill);
        });
        imgWrap.appendChild(badgeWrap);
      }

      link.appendChild(imgWrap);
    }

    // Text content
    var textWrap = el('div', 'v-stack gap-1 sm:gap-1.5');

    // Vendor — only if theme setting says to show it
    if (product.vendor && shouldShowVendor(component)) {
      var vendor = el('span', 'text-xs text-subdued');
      vendor.textContent = product.vendor;
      textWrap.appendChild(vendor);
    }

    var innerWrap = el('div', 'v-stack justify-items-start gap-0.5');

    // Title
    var titleBold = el('span', 'bold');
    var titleSpan = el('span', 'reversed-link');
    titleSpan.textContent = product.title || '';
    titleBold.appendChild(titleSpan);
    innerWrap.appendChild(titleBold);

    // Threadheads Update 2026-05-30: price block intentionally removed.
    // Threadverse search returns AUD prices that the formatter renders
    // with the visitor's local currency symbol — wrong everywhere except
    // AU. PDP/cart/checkout are correct via Shopify Markets, so the only
    // misleading surface is the search widget.
    //
    // The hotfix on 2026-05-28 removed price from the Liquid snippet
    // (snippets/predictive-search-result-item.liquid) and from the
    // headerSearch.ts modal (src/js/sections/headerSearch.ts), but
    // missed this third surface — this file injects its own product-card
    // HTML into the mobile native search drawer, overriding the
    // Liquid snippet's output.
    //
    // When Threadverse later ships per-market pricing, re-add the
    // block with: if (product.price && parseFloat(String(product.price)) > 0)
    // and require a price_currency field in the response.

    // Star rating
    if (product.review_rating && product.review_count > 0) {
      var ratingWrap = el('div', 'th-search-rating');
      var stars = '\u2605'.repeat(Math.round(product.review_rating)) +
                  '\u2606'.repeat(5 - Math.round(product.review_rating));
      var ratingText = el('span', 'th-search-rating__stars');
      ratingText.textContent = stars;
      ratingWrap.appendChild(ratingText);
      var countText = el('span', 'th-search-rating__count');
      countText.textContent = ' (' + product.review_count + ')';
      ratingWrap.appendChild(countText);
      innerWrap.appendChild(ratingWrap);
    }

    textWrap.appendChild(innerWrap);
    link.appendChild(textWrap);

    // Click tracking + purchase attribution
    link.addEventListener('click', function () {
      trackEvent(apiUrl, {
        event_type: 'click',
        query: query,
        product_id: product.shopify_id,
        position: position
      });
      // Threadverse purchase attribution: record the last search click so
      // the Shopify Customer Events pixel can attribute the subsequent
      // checkout_completed to this search session. The pixel reads this
      // key from localStorage, posts a purchase event to Threadverse, and
      // clears it so repeat pixel fires don't double-count.
      // Empty query (idle-state click) is intentional — those clicks still
      // count as search-attributed since the user engaged via the search UI.
      try {
        localStorage.setItem('threadverse_last_search_click', JSON.stringify({
          product_id: product.shopify_id,
          query: query || '',
          session_id: getSessionId(),
          clicked_at: Date.now()
        }));
      } catch (e) {
        // localStorage can fail in private mode or when quota is exceeded.
        // Fail silently — analytics must never break the product click flow.
      }
    });

    return link;
  }

  // Helper: create a tab title element (slot="title")
  function buildTabTitle(label) {
    var div = el('div', 'predictive-search__tab-item', { slot: 'title' });
    var btn = el('button', 'h5', { type: 'button' });
    btn.textContent = label;
    div.appendChild(btn);
    return div;
  }

  // Helper: create a tab content panel (slot="content")
  function buildTabContent(typeClass, sectionHeading) {
    var div = el('div',
      'predictive-search__tab-content predictive-search__tab-content--' + typeClass + ' v-stack gap-4 sm:gap-6',
      { slot: 'content' }
    );
    div.style.setProperty('--section-heading', "'" + sectionHeading + "'");
    return div;
  }

  // Helper: build the <template> child inside <x-tabs> using safe DOM methods
  function buildXTabsTemplate() {
    var template = document.createElement('template');
    var style = document.createElement('style');
    style.textContent =
      '[role="tablist"]::-webkit-scrollbar {display: none;} ' +
      ':host { --predictive-search-column-width: 0px; }';
    var titleSlot = document.createElement('slot');
    titleSlot.setAttribute('role', 'tablist');
    titleSlot.setAttribute('part', 'tab-list');
    titleSlot.setAttribute('name', 'title');
    var contentSlot = document.createElement('slot');
    contentSlot.setAttribute('part', 'tab-panels');
    contentSlot.setAttribute('name', 'content');
    template.content.appendChild(style);
    template.content.appendChild(titleSlot);
    template.content.appendChild(contentSlot);
    return template;
  }

  // Build the full results DOM using x-tabs wrapper structure (matches Liquid template)
  // options.hideViewAll: skip the "View all results" button (used for idle state —
  //   there's no query to view all of when the drawer just opened).
  // options.isIdle: label the suggestions section "Trending" instead of "Suggestions".
  //   Idle suggestions are Mel-curated trending phrases from Threadverse, search-results
  //   suggestions are autocomplete refinements — different content, different label.
  // options.emptyMessage: prepend a banner above the layout (used for empty-results
  //   state to acknowledge the failed query while showing recovery content).
  // options.viewAllOverride: override data.query for the View all button text/href.
  function buildResultsDOM(data, apiUrl, component, options) {
    options = options || {};
    var suggestionsLabel = options.isIdle ? str('searchTrending', 'Trending') : str('searchSuggestions', 'Suggestions');
    var frag = document.createDocumentFragment();
    var query = options.viewAllOverride || data.corrected_query || data.query || '';

    // Empty-results message banner (rendered at the top, before correction)
    if (options.emptyMessage) {
      var emptyBanner = el('div', 'th-search-empty-message');
      emptyBanner.textContent = options.emptyMessage;
      frag.appendChild(emptyBanner);
    }

    // "Did you mean" correction (rendered above x-tabs)
    if (data.corrected_query) {
      var correction = el('p', 'th-search-correction');
      correction.appendChild(text(str('searchShowingResultsFor', 'Showing results for') + ' '));
      var correctedStrong = el('strong');
      correctedStrong.textContent = data.corrected_query;
      correction.appendChild(correctedStrong);
      frag.appendChild(correction);
    }

    // Threadheads Update: use a plain <div> instead of <x-tabs>.
    // Previously this rendered an <x-tabs> custom element with a <template>
    // child, but the base theme's Tabs constructor reads `template.content`
    // synchronously when document.createElement('x-tabs') runs — at which
    // point our <template> child hasn't been appended yet. Result: TypeError
    // "Cannot read properties of null (reading 'content')" on every search.
    // The mobile drawer's CSS overrides x-tabs to flat-stack everything via
    // .predictive-search__tab-content order properties, so we don't actually
    // need the custom element at all. A plain div with the same class works
    // identically and avoids the constructor race.
    var xTabs = el('div', 'predictive-search__tabs');

    // Products tab (rendered first in Liquid template)
    if (data.products && data.products.length > 0) {
      var productsLabel = str('searchProducts', 'Products');
      xTabs.appendChild(buildTabTitle(productsLabel));
      var prodContent = buildTabContent('products', productsLabel);
      data.products.forEach(function (p, i) {
        prodContent.appendChild(buildProductCard(p, i + 1, apiUrl, data.query, component));
      });
      xTabs.appendChild(prodContent);
    }

    // Suggestions / queries tab
    if (data.suggestions && data.suggestions.length > 0) {
      xTabs.appendChild(buildTabTitle(suggestionsLabel));
      var queryContent = buildTabContent('queries', suggestionsLabel);
      data.suggestions.slice(0, 5).forEach(function (s) {
        var link = el('a', 'predictive-search-result bold', {
          href: '/search?q=' + encodeURIComponent(s) + '&type=product'
        });
        var span = el('span', 'reversed-link');
        span.textContent = s;
        link.appendChild(span);
        link.addEventListener('click', function () {
          trackEvent(apiUrl, { event_type: 'click_suggestion', query: s });
        });
        queryContent.appendChild(link);
      });
      xTabs.appendChild(queryContent);
    }

    // Collections tab
    if (data.collections && data.collections.length > 0) {
      var collectionsLabel = str('searchCollections', 'Collections');
      xTabs.appendChild(buildTabTitle(collectionsLabel));
      var collContent = buildTabContent('collections', collectionsLabel);
      data.collections.slice(0, 2).forEach(function (c) {
        var link = el('a', 'predictive-search-result group', {
          href: c.url,
          'data-instant': ''
        });
        link.addEventListener('click', function () {
          trackEvent(apiUrl, { event_type: 'click_collection', query: data.query || '', product_id: c.handle || c.title });
        });
        var textDiv = el('div', 'v-stack gap-0.5');
        var titleSpan = el('span', 'bold');
        var innerTitle = el('span', 'reversed-link');
        innerTitle.textContent = c.title;
        titleSpan.appendChild(innerTitle);
        textDiv.appendChild(titleSpan);
        if (c.products_count) {
          var countSpan = el('span', 'text-sm text-subdued');
          countSpan.textContent = fill(str('searchCollectionCount', '[count] products'), '[count]', String(c.products_count));
          textDiv.appendChild(countSpan);
        }
        link.appendChild(textDiv);
        collContent.appendChild(link);
      });
      xTabs.appendChild(collContent);
    }

    // View all (slot="content" only — no tab title, sits at the bottom)
    // Skipped in idle state where there's no search query to "view all" of.
    if (!options.hideViewAll) {
      var viewAllContent = el('div', 'predictive-search__tab-content predictive-search__tab-content--view-all', { slot: 'content' });
      var viewAllBtn = el('button', 'text-with-icon group', { type: 'submit' });
      var viewAllText = el('span');
      var viewAllInner = el('span', 'reversed-link');
      viewAllInner.appendChild(text(str('searchViewAllPrefix', 'View all results "')));
      var viewAllQuery = el('span', 'bold');
      viewAllQuery.textContent = query;
      viewAllInner.appendChild(viewAllQuery);
      viewAllInner.appendChild(text(str('searchViewAllSuffix', '"')));
      viewAllText.appendChild(viewAllInner);
      viewAllBtn.appendChild(viewAllText);
      viewAllContent.appendChild(viewAllBtn);
      xTabs.appendChild(viewAllContent);
    }

    frag.appendChild(xTabs);
    return frag;
  }

  // Build zero-results DOM. Reuses the idle-state recovery layout (Popular Now
  // products + Trending sidebar) prefixed with a contextual message banner.
  // Falls back to a plain message + suggestions list if the idle data cache
  // hasn't loaded yet (only when a search finishes before the idle fetch that
  // starts as the drawer opens).
  function buildZeroResultsDOM(data, apiUrl, component, query) {
    if (cachedIdleData && cachedIdleData.products && cachedIdleData.products.length > 0) {
      // Preferred path: recovery via cached idle content
      return buildResultsDOM(cachedIdleData, apiUrl, component, {
        hideViewAll: false,
        isIdle: true,
        emptyMessage: fill(str('searchEmptyPopular', 'Hmm, "[query]" stumped us. Here\'s what\'s popular right now:'), '[query]', query),
        viewAllOverride: query
      });
    }

    // Fallback: idle cache not loaded yet. Render a basic message + suggestions
    // from the search response so the user still has something clickable.
    var frag = document.createDocumentFragment();

    if (data.corrected_query) {
      var correction = el('p', 'th-search-correction');
      correction.appendChild(text(str('searchShowingResultsFor', 'Showing results for') + ' '));
      var correctedStrong = el('strong');
      correctedStrong.textContent = data.corrected_query;
      correction.appendChild(correctedStrong);
      frag.appendChild(correction);
    }

    var msg = el('div', 'th-search-empty-message');
    msg.textContent = fill(str('searchEmptyTry', 'Hmm, "[query]" stumped us. Try one of these:'), '[query]', query);
    frag.appendChild(msg);

    if (data.suggestions && data.suggestions.length > 0) {
      var suggestWrap = el('div', 'th-search-zero__suggestions');
      data.suggestions.forEach(function (s) {
        var link = el('a', 'th-search-zero__link', {
          href: '/search?q=' + encodeURIComponent(s) + '&type=product'
        });
        link.textContent = s;
        suggestWrap.appendChild(link);
      });
      frag.appendChild(suggestWrap);
    }

    return frag;
  }

  // Idle state: featured collection products + trending suggestions rendered
  // into the [slot="idle"] of <predictive-search>. Uses the same
  // buildResultsDOM render path as keyword search so the layout, CSS, and
  // behaviour match exactly.
  //
  // Products come from a Shopify collection (configured via theme setting
  // settings.idle_search_collection, passed through as data-idle-collection).
  // Trending suggestions come from Threadverse's empty-query response.
  //
  // Graceful degradation: if the collection fetch fails or returns empty,
  // the existing Liquid-rendered idle slot content is left untouched so
  // the drawer still has something to show.

  // Threadheads Update 2026-11-04: reads the ?view=search-idle feed
  // (templates/collection.search-idle.liquid), which carries only what
  // buildProductCard reads. The template already drops hidden products and
  // sizes the image to 400px; the width is stripped here because
  // buildProductCard appends its own.
  function normaliseIdleProduct(p) {
    var image = (p.image || '').replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, '');
    return {
      shopify_id: p.id,
      handle: p.handle,
      title: p.title,
      vendor: p.vendor,
      url: '/products/' + p.handle,
      image_url: image,
      badges: []
    };
  }

  function loadIdleState(component, apiUrl, collectionHandle) {
    var collectionUrl = '/collections/' + encodeURIComponent(collectionHandle) + '?view=search-idle';
    var shopifyFetch = fetch(collectionUrl)
      .then(function (r) { return r.ok ? r.json() : { products: [] }; })
      .catch(function () { return { products: [] }; });

    var threadverseFetch;
    if (apiUrl) {
      var tvUrl = apiUrl + '/search?q=&limit=6&session_id=' + encodeURIComponent(getSessionId());
      threadverseFetch = fetch(tvUrl)
        .then(function (r) { return r.ok ? r.json() : { suggestions: [] }; })
        .catch(function () { return { suggestions: [] }; });
      // Threadheads Update 2026-11-04: the shopper is now looking at the open
      // drawer while this loads, so a slow Threadverse can't hold back the
      // Popular Now cards. Same 2s budget as keyword search; past it the cards
      // render without the Trending list.
      threadverseFetch = Promise.race([
        threadverseFetch,
        new Promise(function (resolve) {
          setTimeout(function () { resolve({ suggestions: [] }); }, 2000);
        })
      ]);
    } else {
      threadverseFetch = Promise.resolve({ suggestions: [] });
    }

    Promise.all([shopifyFetch, threadverseFetch]).then(function (results) {
      var shopifyData = results[0] || {};
      var threadverseData = results[1] || {};

      var rawProducts = shopifyData.products || [];
      if (rawProducts.length === 0) {
        // Leave existing Liquid-rendered idle slot as fallback
        return;
      }

      var idleData = {
        query: '',
        corrected_query: null,
        products: rawProducts.map(normaliseIdleProduct),
        suggestions: (threadverseData.suggestions || []).slice(0, 6),
        collections: []
      };

      // Cache for empty-results state reuse
      cachedIdleData = idleData;

      var idleSlot = component.querySelector('[slot="idle"]');
      if (!idleSlot) return;

      // Replace the Liquid-rendered idle content with a search-result-style layout.
      while (idleSlot.firstChild) idleSlot.removeChild(idleSlot.firstChild);
      idleSlot.appendChild(buildResultsDOM(idleData, apiUrl || '', component, { hideViewAll: true, isIdle: true }));
    }).catch(function (err) {
      console.warn('[Threadverse] Idle state load failed:', err && err.message);
    });
  }

  // Threadheads Update 2026-11-04: load the idle state when the shopper heads
  // for the search drawer, not on every pageview. It used to fetch and build
  // 8 cards into the closed drawer on DOMContentLoaded (368 KB of JSON), and
  // from 700px up the drawer is never used (headerSearch.ts routes the same
  // trigger to the desktop modal). Earliest signal wins, once per page:
  //  - pointerdown on a search-drawer trigger below 700px, ~100ms before the
  //    click that opens the drawer;
  //  - the drawer's own `toggle` event, fired synchronously when `open` is set,
  //    which also covers programmatic opens (the mobile nav's search bar) and
  //    the theme editor.
  // Until it lands the drawer shows the Liquid-rendered idle menu, as before.
  function initIdleState(component) {
    var handle = component.getAttribute('data-idle-collection');
    if (!handle) return;
    var apiUrl = component.getAttribute('data-threadverse-api');
    var drawer = component.closest('search-drawer');
    var loaded = false;

    function load() {
      if (loaded) return;
      loaded = true;
      document.removeEventListener('pointerdown', onPointerDown, true);
      if (drawer) drawer.removeEventListener('toggle', onToggle);
      loadIdleState(component, apiUrl, handle);
    }

    function onPointerDown(e) {
      if (window.innerWidth >= 700) return;
      if (e.target && e.target.closest && e.target.closest('[aria-controls="search-drawer"]')) load();
    }

    function onToggle(e) {
      if (e.target === drawer && drawer.hasAttribute('open')) load();
    }

    // No drawer around it: keep the old eager load rather than never loading.
    if (!drawer || drawer.hasAttribute('open')) {
      load();
      return;
    }
    document.addEventListener('pointerdown', onPointerDown, true);
    drawer.addEventListener('toggle', onToggle);
  }

  function initIdleStates() {
    var components = document.querySelectorAll('predictive-search[data-idle-collection]');
    for (var i = 0; i < components.length; i++) {
      initIdleState(components[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initIdleStates);
  } else {
    initIdleStates();
  }

  // Main: override predictive-search component
  //
  // IMPORTANT: Impact 5.3.2's <predictive-search> uses a private method
  // `_doPredictiveSearch()` (NOT `fetchResults`). It reads the query from
  // `this._queryInput.value`, sets up `this._abortController` in
  // `_onInputChanged`, and uses `_transitionToSlot('loading'|'results'|'idle')`
  // to animate slot changes. We hook into _doPredictiveSearch so the rest of
  // the component's lifecycle (debounce, abort on new input, slot animations)
  // still works unchanged.
  customElements.whenDefined('predictive-search').then(function () {
    var PredictiveSearch = customElements.get('predictive-search');
    if (!PredictiveSearch) return;

    var originalDoPredictiveSearch = PredictiveSearch.prototype._doPredictiveSearch;
    if (!originalDoPredictiveSearch) {
      console.error('[Threadverse] _doPredictiveSearch not found on PredictiveSearch prototype — base theme may have changed');
      return;
    }

    PredictiveSearch.prototype._doPredictiveSearch = async function () {
      var component = this;
      var apiContainer = component.closest('[data-threadverse-api]');
      var apiUrl = apiContainer ? apiContainer.getAttribute('data-threadverse-api') : null;

      if (!apiUrl) {
        return originalDoPredictiveSearch.call(component);
      }

      var query = component._queryInput ? component._queryInput.value : '';
      if (!query) {
        return originalDoPredictiveSearch.call(component);
      }

      // Transition to loading state (matches base theme behaviour)
      await component._transitionToSlot('loading');

      var url = apiUrl + '/search?q=' + encodeURIComponent(query) +
        '&limit=10&session_id=' + encodeURIComponent(getSessionId());

      // Use the component's own abort signal so debounced keystrokes cancel
      // in-flight requests. Race against a 2s timeout for Threadverse.
      var signal = component._abortController && component._abortController.signal;
      var threadverseFetch = fetch(url, { signal: signal });
      var timeoutPromise = new Promise(function (_, reject) {
        setTimeout(function () { reject(new Error('Threadverse timeout')); }, 2000);
      });

      try {
        var res = await Promise.race([threadverseFetch, timeoutPromise]);
        if (!res.ok) throw new Error('API returned ' + res.status);
        var data = await res.json();

        // Build the new results slot fragment
        var newSlot = el('div');
        newSlot.setAttribute('slot', 'results');

        if (!data.products || data.products.length === 0) {
          newSlot.appendChild(buildZeroResultsDOM(data, apiUrl, component, query));
          scheduleSearchAnalytics(apiUrl, query, 0);
        } else {
          newSlot.appendChild(buildResultsDOM(data, apiUrl, component));
          scheduleSearchAnalytics(apiUrl, query, data.products.length);
        }

        // Replace the existing results slot (matches _doPredictiveSearch pattern)
        var existingSlot = component.querySelector('[slot="results"]');
        if (existingSlot) {
          existingSlot.replaceWith(newSlot);
        } else {
          component.appendChild(newSlot);
        }

        return component._transitionToSlot('results');
      } catch (err) {
        if (err && err.name === 'AbortError') {
          // User typed something new — let the new request handle it
          return;
        }
        console.warn('[Threadverse] Falling back to Shopify:', err && err.message);
        // Fall back to the base theme's native Shopify search
        return originalDoPredictiveSearch.call(component);
      }
    };

    console.log('[Threadverse] Search override installed on _doPredictiveSearch');
  });
})();
